import * as THREE from "three";
import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  clampToPlot,
  clampToInterior,
  supportHeightAt,
  getFootprint,
  placementBounds,
  PLACEMENT_SCALE_MAX_STEP,
  type HomeType,
  type ItemPlacement
} from "@tribe/domain";
import type { PlacedItemsLayer } from "./PlacedItemsLayer";
import type { HandleKind, PlacementHandles } from "./PlacementHandles";

/**
 * Picking things up and putting them down.
 *
 * The camera holds still while an item is selected. Orbiting and dragging both want the same
 * one-finger gesture, and letting them share it means every attempt to move a bench swings the
 * whole village instead. Selecting is the switch: once something is in hand the view is pinned,
 * and it is released when nothing is selected.
 */

type Options = {
  canvas: HTMLCanvasElement;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  layer: PlacedItemsLayer;
  handles: PlacementHandles;
  /** Plot-local voxels, already clamped inside the fence by the caller. */
  onCommit: (placementId: string, position: { x: number; z: number }) => void;
  onSelect: (placementId: string | null) => void;
  onRotate: (placementId: string, quarterTurns: number) => void;
  onScale: (placementId: string, scaleStep: number) => void;
  onRemove: (placementId: string) => void;
  getSelected: () => string | null;
  getPlacements: () => ItemPlacement[];
  getPlotTier: () => number;
  /** The home whose interior is being arranged, or null outdoors. */
  getHomeType: () => HomeType | null;
};

/** Vertical pixels of drag per quarter turn. */
const PIXELS_PER_TURN = 46;
/** Pixels the scale grip travels per size step. */
const PIXELS_PER_STEP = 40;

type HandleDrag = {
  kind: "rotate" | "scale";
  placementId: string;
  originX: number;
  originY: number;
  /** The value the item had when the grip was taken hold of. */
  startValue: number;
  /** Pixels between the item and the pointer at that moment, for the scale grip. */
  startReach: number;
};

export class PlacementControls {
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly hit = new THREE.Vector3();
  private dragging: { placementId: string; grabOffsetX: number; grabOffsetZ: number } | null = null;
  private handleDrag: HandleDrag | null = null;
  private moved = false;

  constructor(private readonly options: Options) {
    // Capture phase, because OrbitControls listens on this same canvas and would otherwise start
    // an orbit on the very gesture meant to pick something up.
    options.canvas.addEventListener("pointerdown", this.onPointerDown, { capture: true });
    options.canvas.addEventListener("pointermove", this.onPointerMove);
    options.canvas.addEventListener("pointerup", this.onPointerUp);
    options.canvas.addEventListener("pointercancel", this.onPointerUp);
  }

  /**
   * Whether this instance is the one listening.
   *
   * Two of these share the canvas — the yard's and the home's — and only one side of the walls is
   * ever being arranged. Without this both would raycast every pointer down, and a click meant for
   * a chair indoors would also deselect whatever was selected in the garden.
   */
  private active = true;

  public setActive(active: boolean): void {
    this.active = active;
    if (!active) {
      this.dragging = null;
      this.handleDrag = null;
    }
  }

  /** Pins the camera while something is in hand. */
  public setCameraLocked(locked: boolean): void {
    this.options.controls.enabled = !locked;
  }

  private toPlotLocal(event: PointerEvent): { x: number; z: number } | null {
    const rect = this.options.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.options.camera);

    // Intersect the parcel's ground in world space, then let the layer's own frame do the
    // conversion. The frame already carries the plot's rotation, so this is the only place the
    // two coordinate systems ever meet.
    const frame = this.options.layer.frame;
    const worldPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -frame.getWorldPosition(new THREE.Vector3()).y);
    if (!this.raycaster.ray.intersectPlane(worldPlane, this.hit)) return null;

    const local = frame.worldToLocal(this.hit.clone());
    return { x: local.x / 0.5, z: local.z / 0.5 };
  }

  private placementUnder(event: PointerEvent): string | null {
    const rect = this.options.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.options.camera);
    const hits = this.raycaster.intersectObjects(this.options.layer.pickables, true);
    for (const entry of hits) {
      let node: THREE.Object3D | null = entry.object;
      while (node) {
        const id = node.userData?.placementId;
        if (typeof id === "string") return id;
        node = node.parent;
      }
    }
    return null;
  }

  /** Which grip, if any, is under the pointer. Grips are tested before items so they stay usable
   *  even when they overlap the thing they belong to. */
  private handleUnder(event: PointerEvent): HandleKind | null {
    const pickables = this.options.handles.pickables;
    if (!pickables.length) return null;
    const rect = this.options.canvas.getBoundingClientRect();
    this.pointer.set(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    this.raycaster.setFromCamera(this.pointer, this.options.camera);
    for (const entry of this.raycaster.intersectObjects(pickables, false)) {
      const kind = this.options.handles.kindOf(entry.object);
      if (kind) return kind;
    }
    return null;
  }

  private onPointerDown = (event: PointerEvent) => {
    if (!this.active) return;
    if (event.button !== 0 && event.pointerType === "mouse") return;

    const selected = this.options.getSelected();
    const handle = selected ? this.handleUnder(event) : null;
    if (handle && selected) {
      event.stopPropagation();
      event.preventDefault();
      const placement = this.options.getPlacements().find((entry) => entry.id === selected);
      if (!placement) return;

      if (handle === "delete") {
        this.options.onRemove(selected);
        return;
      }

      const centre = this.itemScreenPosition(selected);
      this.handleDrag = {
        kind: handle,
        placementId: selected,
        originX: event.clientX,
        originY: event.clientY,
        startValue: handle === "rotate" ? placement.rotationQuarters : placement.scaleStep,
        startReach: centre ? Math.hypot(event.clientX - centre.x, event.clientY - centre.y) : 0
      };
      this.capture(event.pointerId);
      return;
    }

    const placementId = this.placementUnder(event);
    if (!placementId) {
      // Tapping bare ground while holding something puts it down and hands the camera back.
      if (this.options.getSelected()) this.options.onSelect(null);
      return;
    }

    // Only now is it certain this gesture belongs to an item rather than to the camera.
    event.stopPropagation();
    event.preventDefault();

    const placement = this.options.getPlacements().find((entry) => entry.id === placementId);
    const local = this.toPlotLocal(event);
    if (!placement || !local) return;

    this.options.onSelect(placementId);
    this.dragging = {
      placementId,
      // Grabbing an item by its edge should not snap its centre to the cursor.
      grabOffsetX: placement.position.x - local.x,
      grabOffsetZ: placement.position.z - local.z
    };
    this.moved = false;
    this.capture(event.pointerId);
  };

  private onPointerMove = (event: PointerEvent) => {
    if (!this.active) return;
    if (this.handleDrag) {
      this.dragHandle(event);
      return;
    }
    if (!this.dragging) return;
    const local = this.toPlotLocal(event);
    if (!local) return;

    const placement = this.options.getPlacements().find((entry) => entry.id === this.dragging!.placementId);
    if (!placement) return;

    const wanted = {
      x: local.x + this.dragging.grabOffsetX,
      z: local.z + this.dragging.grabOffsetZ
    };
    const bounds = placementBounds(
      getFootprint(placement.itemId), placement.rotationQuarters, placement.scaleStep
    );

    const homeType = this.options.getHomeType();
    if (placement.surface === "interior" && homeType) {
      const clamped = clampToInterior({ position: wanted, bounds, homeType });
      // The height is not dragged, it is discovered: whatever the item is over decides where it
      // sits. Dragging a plant onto a table lifts it as it crosses the edge.
      const y = supportHeightAt({
        position: clamped,
        bounds,
        placements: this.options.getPlacements().filter((entry) => entry.surface === "interior"),
        ignoreId: placement.id
      });
      this.options.layer.previewPosition(this.dragging.placementId, { ...clamped, y });
      this.moved = true;
      return;
    }

    const clamped = clampToPlot({ position: wanted, bounds, plotTier: this.options.getPlotTier() });

    // Straight to the mesh at pointer speed. The store only hears about it when the item lands.
    this.options.layer.previewPosition(this.dragging.placementId, clamped);
    this.moved = true;
  };

  /**
   * Turning and resizing, both by dragging a grip.
   *
   * Rotation reads the drag up the screen and scaling reads it away from the item, and both land
   * on whole steps as they go — the member chose snap-to-quarter-turns, and there are only four
   * sizes, so there is nothing in between to show.
   */
  private dragHandle(event: PointerEvent): void {
    const drag = this.handleDrag!;

    if (drag.kind === "rotate") {
      // Up turns one way, down the other.
      const steps = Math.round((drag.originY - event.clientY) / PIXELS_PER_TURN);
      const wanted = (((drag.startValue + steps) % 4) + 4) % 4;
      const placement = this.options.getPlacements().find((entry) => entry.id === drag.placementId);
      if (placement && placement.rotationQuarters !== wanted) {
        this.options.onRotate(drag.placementId, wanted - placement.rotationQuarters);
      }
      return;
    }

    // Pull away from the item to grow it, back toward it to shrink. Measured against the item
    // itself rather than the grip, because the grip moves as the item resizes and would chase
    // the finger that was moving it.
    const centre = this.itemScreenPosition(drag.placementId);
    if (!centre) return;
    const reach = Math.hypot(event.clientX - centre.x, event.clientY - centre.y);
    const steps = Math.round((reach - drag.startReach) / PIXELS_PER_STEP);
    const wanted = Math.min(PLACEMENT_SCALE_MAX_STEP, Math.max(0, drag.startValue + steps));
    const placement = this.options.getPlacements().find((entry) => entry.id === drag.placementId);
    if (placement && placement.scaleStep !== wanted) this.options.onScale(drag.placementId, wanted);
  }

  /**
   * Holding onto the pointer for the length of a drag, so it keeps reporting even when it leaves
   * the canvas. Best-effort: a browser that refuses must not take the drag down with it.
   */
  private capture(pointerId: number): void {
    try {
      this.options.canvas.setPointerCapture(pointerId);
    } catch {
      // Without capture the drag still works while the pointer stays over the canvas.
    }
  }

  private release(pointerId: number): void {
    try {
      if (this.options.canvas.hasPointerCapture(pointerId)) {
        this.options.canvas.releasePointerCapture(pointerId);
      }
    } catch {
      // Nothing to release.
    }
  }

  /** Where an item sits on screen, for measuring a grip drag against. */
  private itemScreenPosition(placementId: string): { x: number; y: number } | null {
    const group = this.options.layer.groupFor(placementId);
    if (!group) return null;
    const rect = this.options.canvas.getBoundingClientRect();
    const projected = group.getWorldPosition(new THREE.Vector3()).project(this.options.camera);
    return {
      x: rect.left + ((projected.x + 1) / 2) * rect.width,
      y: rect.top + ((-projected.y + 1) / 2) * rect.height
    };
  }

  private onPointerUp = (event: PointerEvent) => {
    if (this.handleDrag) {
      this.handleDrag = null;
      this.release(event.pointerId);
      return;
    }
    if (!this.dragging) return;
    const { placementId, grabOffsetX, grabOffsetZ } = this.dragging;
    this.dragging = null;
    this.release(event.pointerId);
    if (!this.moved) return;

    const local = this.toPlotLocal(event);
    if (!local) return;

    // The same offset the preview has been drawing with. Committing the bare pointer position
    // instead would snap the item's centre to the cursor the instant it was let go.
    this.options.onCommit(placementId, { x: local.x + grabOffsetX, z: local.z + grabOffsetZ });
  };

  public destroy(): void {
    const { canvas } = this.options;
    canvas.removeEventListener("pointerdown", this.onPointerDown, { capture: true });
    canvas.removeEventListener("pointermove", this.onPointerMove);
    canvas.removeEventListener("pointerup", this.onPointerUp);
    canvas.removeEventListener("pointercancel", this.onPointerUp);
    this.options.controls.enabled = true;
  }
}
